import type { Model, StreamEvent, Message, MessagePart } from '../agents/types';
import type { ModelProviderAdapter, StreamRequest } from './ModelProviderAdapter';
import { shortId } from '../utils';
import { useUIStore } from '../store/uiStore';

const MODELS: Model[] = [
  { id: 'gpt-5', label: 'GPT-5', providerId: 'openai' },
  { id: 'gpt-5-mini', label: 'GPT-5 mini', providerId: 'openai' },
  { id: 'o4', label: 'o4', providerId: 'openai' },
];

function getKey(): string | undefined {
  return useUIStore.getState().apiKeys.openai;
}

function toOpenAIMessages(messages: Message[]): Array<{ role: string; content: string }> {
  return messages.map((m) => ({
    role: m.role === 'system' ? 'system' : m.role,
    content: m.parts
      .filter((p: MessagePart) => p.type === 'text')
      .map((p) => (p as { type: 'text'; text: string }).text)
      .join('\n\n'),
  })).filter((m) => m.content.length > 0);
}

export const OpenAIAdapter: ModelProviderAdapter = {
  id: 'openai',
  label: 'OpenAI',
  get configured() {
    return !!getKey();
  },
  listModels: () => MODELS,

  async *stream(req: StreamRequest): AsyncIterable<StreamEvent> {
    const key = getKey();
    if (!key) {
      throw new Error('OpenAI API key not configured. Open Settings to add one.');
    }

    const messageId = shortId('msg');
    yield { type: 'message-start', messageId, role: 'assistant' };
    yield { type: 'status', messageId, status: 'streaming' };

    const body = {
      model: req.modelId,
      stream: true,
      messages: toOpenAIMessages(req.messages),
    };

    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${key}`,
      },
      body: JSON.stringify(body),
      signal: req.signal,
    });

    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => '');
      throw new Error(`OpenAI ${res.status}: ${text || res.statusText}`);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    try {
      while (true) {
        if (req.signal?.aborted) break;
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // OpenAI SSE — events delimited by double newlines, each line `data: …`
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? ''; // keep tail for next chunk

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (!payload || payload === '[DONE]') continue;
          try {
            const obj = JSON.parse(payload) as {
              choices?: Array<{ delta?: { content?: string } }>;
            };
            const delta = obj.choices?.[0]?.delta?.content;
            if (delta) yield { type: 'text-delta', messageId, delta };
          } catch {
            // ignore parse failures (keep-alive comments etc.)
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    yield { type: 'message-end', messageId };
  },
};
